import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody, Chip, Typography, Alert,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileUploadField } from '../../components/FileUploadField';
import { vendorsApi } from '../../api/vendors.api';
import { extractApiError } from '../../api/error';

interface Props {
  vendorId: number | null;
  vendorName?: string;
  onClose: () => void;
}

const DOC_TYPES = [
  { value: 'gst_certificate', label: 'GST Certificate' },
  { value: 'pan_card', label: 'PAN Card' },
  { value: 'cancelled_cheque', label: 'Cancelled Cheque' },
  { value: 'other', label: 'Other' },
];

export function VendorDocumentsDialog({ vendorId, vendorName, onClose }: Props) {
  const qc = useQueryClient();
  const open = vendorId !== null;
  const [docType, setDocType] = useState('gst_certificate');
  const [fileName, setFileName] = useState('');
  const [fileUrl, setFileUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: docs = [] } = useQuery({
    queryKey: ['vendor-docs', vendorId],
    queryFn: () => vendorsApi.listDocuments(vendorId as number),
    enabled: open,
  });

  const addMut = useMutation({
    mutationFn: () => vendorsApi.addDocument(vendorId as number, { docType, fileName, fileUrl }),
    onSuccess: () => {
      setFileName(''); setFileUrl(''); setError(null);
      qc.invalidateQueries({ queryKey: ['vendor-docs', vendorId] });
    },
    onError: (e) => setError(extractApiError(e)),
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Documents — {vendorName}</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle2" gutterBottom>Add document (new version)</Typography>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <Stack direction="row" spacing={2} mb={2}>
          <TextField select size="small" label="Type" value={docType} onChange={(e) => setDocType(e.target.value)} sx={{ minWidth: 180 }}>
            {DOC_TYPES.map((d) => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
          </TextField>
          <TextField size="small" label="File name" value={fileName} onChange={(e) => setFileName(e.target.value)} />
          <FileUploadField label="Document" value={fileUrl || null} onChange={(url) => setFileUrl(url ?? '')} />
          <Button variant="contained" disabled={!fileName || !fileUrl} onClick={() => addMut.mutate()}>Add</Button>
        </Stack>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell><TableCell>File</TableCell><TableCell>Version</TableCell><TableCell>Current</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {docs.map((d) => (
              <TableRow key={d.id}>
                <TableCell>{d.docType}</TableCell>
                <TableCell>{d.fileName}</TableCell>
                <TableCell>v{d.version}</TableCell>
                <TableCell>{d.isCurrent ? <Chip size="small" color="success" label="current" /> : '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
